import type { UserProfile as IdTokenClaims } from "oidc-client-ts";

import ApiService from "../services/api";

const LOGIN_EVENTS_PATH = "/users/login-events";
const LAST_REGISTERED_SESSION_KEY = "uptime.lastRegisteredLoginSession";

const isLoginAuditEnabled = () => import.meta.env.VITE_LOGIN_AUDIT_ENABLED === "true";

/**
 * Identifies the IdP authentication behind a token. `auth_time` (falling back to `sid`)
 * only changes on a real sign-in at RIO, not on silent token renewals or page reloads.
 */
const getLoginSessionKey = (idToken?: IdTokenClaims): string | null => {
  const authTime = idToken?.auth_time;
  if (authTime) return `auth_time:${authTime}`;

  const sid = idToken?.sid;
  if (typeof sid === "string" && sid) return `sid:${sid}`;

  return null;
};

const getLastRegisteredSession = (): string | null => {
  try {
    return localStorage.getItem(LAST_REGISTERED_SESSION_KEY);
  } catch {
    return null;
  }
};

const saveLastRegisteredSession = (sessionKey: string): void => {
  try {
    localStorage.setItem(LAST_REGISTERED_SESSION_KEY, sessionKey);
  } catch {
    // ignore
  }
};

/**
 * Registers a fresh login on the backend. Fire-and-forget: it never throws,
 * so a failure here can't block the app from rendering.
 * The user is identified by the bearer token, so no user data is sent in the body.
 */
export const registerLoginEvent = async (token: string): Promise<void> => {
  if (!isLoginAuditEnabled()) return;

  try {
    await new ApiService().post(
      LOGIN_EVENTS_PATH,
      { occurredAt: new Date().toISOString() },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } catch (error) {
    console.warn("Could not register login event:", error);
  }
};

/**
 * Called on every user load (initial sign-in and each silent renew). Registers the login
 * only once per IdP authentication, regardless of how the user reached the app
 * (direct redirect, RIO home, another tab). The key is saved before the request so
 * tabs opening at the same time don't register it twice.
 */
export const registerLoginIfNewSession = (token: string | null | undefined, idToken?: IdTokenClaims): void => {
  if (!isLoginAuditEnabled() || !token) return;

  const sessionKey = getLoginSessionKey(idToken);
  if (!sessionKey || sessionKey === getLastRegisteredSession()) return;

  saveLastRegisteredSession(sessionKey);
  void registerLoginEvent(token);
};
